import { useTranslation } from "react-i18next";

import type { Progress } from "../../configs/apis/GetProgress/models";
import type { Prize } from "../../configs/apis/models";
import { LIMIT_TO_SEE_ALL } from "../../configs/models/constants";
import {
  getActiveMilestonePrize,
  getShowablePrize,
  isPendingOrProcessingPrize,
} from "../../configs/utils/utility";
import type { ChallengeVariant } from "../Cards/ChallengeCard";
import ChallengeCard from "../Cards/ChallengeCard";
import Skeleton from "../Common/Skeleton";
import SectionLayout from "../Layouts/SectionLayout";

type SliderProps = {
  claimingChallengeId?: number;
  content: Progress[];
  isClaiming: boolean;
  isLoading: boolean;
  onClaim: (challengeId?: number, reward?: Prize | undefined) => void;
  count: number;
};

const Slider = ({ claimingChallengeId, content, count, isClaiming, isLoading, onClaim }: SliderProps) => {
  const { t: translate } = useTranslation("translation");

  // Pending counts as claimable because ChallengeCard renders it as a disabled
  // spinner rather than a button.
  const getVariant = (card: Progress): ChallengeVariant => {
    if (getShowablePrize(card?.prizes) || isPendingOrProcessingPrize(card?.prizes)) {
      return "claimable";
    }
    return "inProgress";
  };

  return (
    <SectionLayout
      count={count}
      hasSeeAll={count > LIMIT_TO_SEE_ALL}
      title={translate("in_progress")}
      to="/gamification/all-in-progress"
    >
      {isLoading
        ? Array.from({ length: 4 }).map((_, idx) => <Skeleton key={idx} />)
        : content?.map((slide) => {
          // activePrize drives the card's status affordance; showablePrize is what the
          // popup may render. They differ when the reward is not usable yet.
          const activePrize = getActiveMilestonePrize(slide?.prizes);
          const showablePrize = getShowablePrize(slide?.prizes);

          return (
            <ChallengeCard
              {...slide}
              categoryName={slide?.category?.name || ""}
              isClaimingThis={isClaiming && claimingChallengeId === slide.id}
              key={slide.id}
              onClaim={() => onClaim(slide.id, showablePrize)}
              prize={activePrize}
              progress={slide?.averageProgress}
              skipImmediateClaim={true}
              variant={getVariant(slide)}
            />
          );
        })}
    </SectionLayout>
  );
};

export default Slider;
